import { reactive } from 'vue';
import { DateTime } from 'luxon';

import ErrorLog from './lib/ErrorLog';
import Deferred from './lib/util/Deferred';

function formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
}

const recorderServiceUuid = '13333333-3333-3333-3333-333333333337';
const characteristics = {
    cmd: '13333333-3333-3333-3333-333333330001',
    vu: '13333333-3333-3333-3333-333333330002',
};

const cachedCharacteristics = {};
const bluetoothMsgBuffer = [];

class GoBluetoothClient {
    /**
     * Initialize GoConnect
     */
    init() {
        this.disconnectCallback = null;
        this.device = reactive({
            name: '',
            installation_type: 1,
            soundcards: [],
            defaultSoundcard: 'alsa_input.platform-dual-soundcard.analog-stereo',
            inputGain: 65536,
            recording: {
                awaitingStart: false,
                awaitingStop: false,
                isRecording: false,
                fileSize: 0,
                fileName: '',
                duration: '00:00:00',
            },
            hasInternetAccess: false,
            networkAdapters: {},
            wifiNetworks: [],
            dmcIsEnabled: undefined,
            dmcIsConnected: undefined,
            dmcIsRunning: false,
            dmcCacheSize: 0,
            dmcMessages: [],
            audioLoopbackStatus: false
        });
        this.vu = reactive({
            left: -80,
            right: -80,
        });
        this.loading = reactive({
            networkAdapters: true,
            wifiNetworks: false,
            djrec: true,
            dmc: true,
            lastMessage: null,
        });

        this.errors = new ErrorLog();
        this.wifiErrors = new ErrorLog();

        setInterval(() => {
            if (!this.loading.lastMessage) {
                return;
            }

            if (this.loading.lastMessage < Date.now() - 5000) {
                this.loading.djrec = true;
            }
        }, 2500);
    }

    handleError(level, message) {
        this.errors.push(level, message);
    }

    handleMessage(event) {
        try {
            const json = JSON.parse(new TextDecoder('UTF-8').decode(event.target.value));
            const keys = Object.keys(json);

            if (json[keys[0]] === '#XdjmENDX#') {
                const textMessage = `[${bluetoothMsgBuffer[keys[0]].replace(/}\n{/g, '},{')}]`;
                const jsonMessage = JSON.parse(textMessage);

                jsonMessage.forEach((message) => this.processMessage(message));

                json[keys[0]] = undefined;
            } else {
                if (bluetoothMsgBuffer[keys[0]] === undefined) {
                    bluetoothMsgBuffer[keys[0]] = '';
                }

                bluetoothMsgBuffer[keys[0]] += json[keys[0]];
            }
        } catch (error) {
            console.log(error);
        }
    }

    /**
     * Process message received from the Bluetooth interface
     *
     * @param {*} message
     */
    processMessage(message) {
        switch (message.type) {
        case 'recorder_type_id':
            this.device.installation_type = message.recorder_type_id;
            break;
        case 'sound_cards':
            this.device.soundcards = message.sound_cards;
            this.device.defaultSoundcard = message.current_default;
            break;
        case 'vu_data':
            this.loading.djrec = false;
            this.loading.lastMessage = Date.now();
            this.vu.left = message.l;
            this.vu.right = message.r;
            this.device.audioLoopbackStatus = message.a;

            if (message.recording_status) {
                if (this.device.recording.awaitingStart) {
                    this.device.recording.awaitingStart = false;
                }

                this.parseRecordingStatusV1(message.recording_status);
            } else {
                if (this.device.recording.awaitingStop) {
                    this.device.recording.awaitingStop = false;
                }
                this.device.recording.duration = '00:00:00';
                this.device.recording.isRecording = false;
                this.device.recording.fileSize = formatBytes(0);
            }

            break;
        case 'wifi_connect':
            if (!(this.wifiConnectionAttempt instanceof Deferred)) {
                break;
            }

            if (message.result.wifi_connect_successful) {
                this.wifiConnectionAttempt.resolve('Connection succesful');
            } else if (!message.result.wifi_connect_successful) {
                switch (message.result.failed_reason) {
                case 'pass_incorrect':
                    this.wifiConnectionAttempt.reject('Wifi password is incorrect, please try again with the correct password');
                    break;
                case 'ssid_not_found':
                    this.wifiConnectionAttempt.reject('Network SSID does not exist');
                    break;
                default:
                    this.wifiConnectionAttempt.reject(message.result.failed_reason);
                    break;
                }
            }

            break;
        case 'has_internet':
            message.ping.forEach((adapter) => {
                if (adapter.internet_access && !adapter.ping_result.includes('FAIL')) {
                    this.device.hasInternetAccess = true;
                }

                this.device.networkAdapters[adapter.adapter_name] = adapter;
            });

            this.loading.networkAdapters = false;

            break;
        case 'current_ethernet':
            break;
        case 'current_wifi':
            break;
        case 'networks':
            break;
        case 'clear_networks':
            this.device.wifiNetworks = [];
            break;
        case 'network':
            this.loading.wifiNetworks = false;
            if (!message.network) {
                break;
            }

            this.device.wifiNetworks.push(message.network);
            break;
        case 'input_gain_info':
            this.device.inputGain = message.inputGain.inputGain;
            break;
        case 'dmc_status':
            this.loading.dmc = false;
            if(message.isDMCEnabled) {
                this.device.dmcIsEnabled = message.isDMCEnabled;
            }

            if(message.isDMCConnected) {
                this.device.dmcIsConnected = message.isDMCConnected;
            }

            this.device.dmcIsRunning = message.isDMCRunning;
            this.device.dmcCacheSize = message.cacheEntries;
            this.device.dmcMessages = message.messages;
            break;
        default:
            console.log(`Unknown message: ${JSON.stringify(message)}`);
            break;
        }
    }

    /**
     * Parse v1 recording status (GoMonitor version < 0.8.5)
     *
     * @param {*} recordingStatus
     */
    parseRecordingStatusV1(recordingStatus) {
        const parsedRequestStatus = recordingStatus.split(' ');

        if (Number.isNaN(parsedRequestStatus[0])) {
            this.device.recording.duration = '00:00:00';
            this.device.recording.isRecording = false;
            this.device.recording.fileSize = formatBytes(0);
        } else {
            // "/recordings/$country/$venue/$stage/$year/$month/$venue-$stage-YYYYMMDD-HHMMSS.djmgoaudio"
            const explodedPath = parsedRequestStatus[1].split('/');
            const explodedFileName = explodedPath[7].split('-');
            const rawDate = explodedFileName[2];
            const rawTime = explodedFileName[3].split('.')[0];

            const startTime = DateTime.local(
                Number(rawDate.substring(0, 4)),
                Number(rawDate.substring(4, 6)),
                Number(rawDate.substring(6, 8)),
                Number(rawTime.substring(0, 2)),
                Number(rawTime.substring(2, 4)),
                Number(rawTime.substring(4, 6)),
            );

            const endTime = DateTime.now();

            this.device.recording.duration = endTime.diff(startTime).toFormat('hh:mm:ss');
            this.device.recording.isRecording = true;
            this.device.recording.fileSize = formatBytes(parsedRequestStatus[0]);
        }
    }

    /**
     * Sends a message, if error retry after 1000ms
     *
     * @param {*} message
     * @returns
     */
    sendMessage(message, attempts = 1) {
        if (attempts > 3) {
            console.log(`Max Attempts(3) to send message exceeeded, message: ${JSON.stringify(message)}`);

            return;
        }

        if (!this.bluetoothDevice.gatt.connected) {
            console.log('Bluetooth device not connect');

            //this.onDisconnect();
            return;
        }

        const characteristic = cachedCharacteristics.cmd;

        if (characteristic == null) {
            throw new Error('bluetooth not ready!');
        }

        const encoder = new TextEncoder('utf-8');
        const buffer = encoder.encode(JSON.stringify(message));

        return characteristic.writeValue(buffer).then(() => {
            const result = new TextDecoder('utf-8').decode(characteristic.value);

            return result;
        }).catch((error) => {
            setTimeout(() => {
                console.debug(error);
                this.sendMessage(message, attempts + 1);
            }, 1000);
        });
    }

    /**
     * Fetch wifi networks from GoMonitor
     */
    fetchWifiNetworks() {
        this.sendMessage({ type: 'get_wifi_networks' });
        this.loading.wifiNetworks = true;
    }

    /**
     * Connect GoMonitor to Wi-Fi network
     *
     * @param {*} ssid
     * @param {*} password
     * @returns
     */
    async connectWifiNetwork(ssid, password) {
        this.wifiConnectionAttempt = new Deferred();
        this.sendMessage({ type: 'wifi_connect', ssid, password });

        return this.wifiConnectionAttempt.promise;
    }

    /**
     * Forget Wi-Fi network on GoMonitor
     */
    forgetWifiNetwork(ssid) {
        this.sendMessage({ type: 'wifi_forget', ssid });
    }

    /**
     * Request input gain from GoMonitor
     */
    fetchInputGain() {
        this.sendMessage({ type: 'get_input_gain' });
    }

    /**
     * Set default soundcard
     */
    setDefaultSoundcard() {
        this.sendMessage({ type: 'set_default_soundcard', soundcard: this.device.defaultSoundcard });
    }

    /**
     * Set input gain on GoMonitor
     *
     * @param {*} value
     */
    setInputGain(value) {
        this.sendMessage({ type: 'set_input_gain', input_gain: value });
    }

    /**
     * Toggle recording
     */
    toggleRecording() {
        if (this.device.recording.isRecording) {
            this.stopRecording();
        } else {
            this.startRecording();
        }
    }

    /**
     * Start a new recording on the GoMonitor
     */
    startRecording() {
        this.device.recording.awaitingStart = true;
        this.sendMessage({ type: 'djpad_recording_start' });

        setTimeout(() => {
            if (this.device.recording.awaitingStart === true) {
                this.errors.push('error', 'Start recording timed out');

                this.device.recording.awaitingStart = false;
            }
        }, 5000);
    }

    /**
     * Stop the current recording on the GoMonitor can be overwritten by the DMT Scheduler
     */
    stopRecording() {
        this.device.recording.awaitingStop = true;
        this.sendMessage({ type: 'djpad_recording_stop' });

        setTimeout(() => {
            if (this.device.recording.awaitingStop === true) {
                this.errors.push('error', 'Stop recording timed out');

                this.device.recording.awaitingStop = false;
            }
        }, 10000);
    }

    /**
     * Start Audio Loopback
     */
    async startAudioLoopback() {
        await this.sendMessage({type: 'audio_loopback_start'});
    }

    /**
     * Stop Audio Loopback
     */
    async stopAudioLoopback() {
        await this.sendMessage({type: 'audio_loopback_stop'});
    }

    /**
     * Shutdown 
     */
    async shutdown() {
        await this.sendMessage({type: 'shutdown'});
    }

    /**
     * Connect to the GoMonitor Bluetooth interface
     *
     * @returns
     */
    async connect() {
        try {
            this.bluetoothDevice = await navigator.bluetooth.requestDevice({
                filters: [{ services: [recorderServiceUuid] }],
            });
        } catch (error) {
            if (error.message === 'User cancelled the requestDevice() chooser.') {
                return;
            }

            throw error;
        }

        this.device.name = this.bluetoothDevice.name;
        this.bluetoothDevice.addEventListener('gattserverdisconnected', this.onDisconnect.bind(this));

        this.bluetoothServer = await this.bluetoothDevice.gatt.connect();

        const service = await this.bluetoothServer.getPrimaryService(recorderServiceUuid);

        const characteristicObjs = await Promise.all(Object.values(characteristics).map(
            (uuid) => service.getCharacteristic(uuid),
        ));

        Object.keys(characteristics).forEach((name, i) => {
            cachedCharacteristics[name] = characteristicObjs[i];
        });

        cachedCharacteristics.vu.addEventListener('characteristicvaluechanged', this.handleMessage.bind(this));
        cachedCharacteristics.vu.startNotifications();

        /**
         * @todo get internet status every x seconds
         */
        setTimeout(async () => {
            await this.sendMessage({ type: 'get_device_type' });
            await this.sendMessage({ type: 'get_soundcards' });
            await this.sendMessage({ type: 'get_input_gain' });
        }, 200);
    }

    /**
     * Disconnect Bluetooth Interface
     *
     * @todo Actual disconnect
     */
    disconnect() {
        if (!this.bluetoothDevice) {
            return;
        }

        if (this.bluetoothDevice.gatt.connected) {
            cachedCharacteristics.vu.stopNotifications();
            cachedCharacteristics.vu.removeEventListener('characteristicvaluechanged', this.handleMessage.bind(this));
            delete cachedCharacteristics.vu;
            delete cachedCharacteristics.cmd;

            this.bluetoothDevice.gatt.disconnect();
        }
    }

    /**
     * Runs when we detect a disconnect from Bluetooth
     */
    onDisconnect() {
        if (this.device.name) {
            this.handleError('info', `Device ${this.device.name} disconnected`);
        }

        if (this.bluetoothDevice) {
            this.bluetoothDevice.forget();
        }

        this.device.name = '';
        this.device.installation_type = 1;
        this.device.soundcards = [];
        this.device.defaultSoundcard = 'alsa_input.platform-dual-soundcard.analog-stereo';
        this.device.inputGain = 65536;
        this.device.recording.isRecording = false;
        this.device.recording.fileSize = 0;
        this.device.recording.fileName = '';
        this.device.recording.duration = '00:00:00';
        this.device.hasInternetAccess = false;
        this.device.networkAdapters = {};
        this.device.wifiNetworks = [];

        if (this.disconnectCallback) {
            this.disconnectCallback();
        }
    }
}

export default GoBluetoothClient;
